import {
  type CSSProperties,
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import mergeRefs from 'merge-refs';
import { cn } from '../platform/dom/cn';

export interface ScrollShadowWrapperProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /**
   * Scroll axis. A horizontal `edge="shadow"` scroller sits in a wrapper that
   * holds the shadows; `className`, `style` and the ref still target the
   * scroller.
   * @default 'vertical'
   */
  orientation?: 'vertical' | 'horizontal';
  /**
   * How an edge with content scrolled past it shows that.
   *
   * - `soft`: the content fades out towards the edge (a `mask-image`) and a
   *   soft shadow lies over the faded edge, without a border. It shows next to
   *   a dark or same-coloured surface, in both themes. The wrapper is a layout
   *   box: `className` sizes it (`flex-1`, `h-64`), `scrollerClassName` styles
   *   the scroller inside it, and the ref and `style` target the scroller. The
   *   mask fades the scroller's own background and border too: put those on a
   *   parent. The faded edges are the scroller's `scroll-padding`, so focus and
   *   `scrollIntoView` bring content in clear of them.
   * - `shadow`: a faint shadow with a hairline over the content at the edge.
   *   Deprecated: migrate to `soft`.
   * - `fade`: the mask without a shadow. Deprecated: migrate to `soft`.
   *
   * `shadow` and `fade` keep `className`, `style` and the ref on the scroller.
   * @default 'shadow'
   */
  edge?: 'soft' | 'shadow' | 'fade';
  /** `edge="soft"` only: classes of the scroller inside the wrapper. */
  scrollerClassName?: string;
  /**
   * Horizontal `edge="soft"` or `edge="fade"` only: a chevron button at each
   * edge with content scrolled past it, which scrolls about a page that way;
   * the content fades out under it. Pointer-only, as keyboard focus scrolls on
   * its own.
   */
  scrollButtons?: boolean;
  // set it to false if you want to style the edge yourself using data-has-top-shadow
  renderTopShadow?: boolean;
  // set it to false if you want to style the edge yourself using data-has-bottom-shadow
  renderBottomShadow?: boolean;
  // set it to false if you want to style the edge yourself using data-has-left-shadow
  renderLeftShadow?: boolean;
  // set it to false if you want to style the edge yourself using data-has-right-shadow
  renderRightShadow?: boolean;
}

const FADE_SIZE = '1.5rem';
// A scroll button's footprint at its edge (`left-0.5` + `size-7`, and a hair):
// the content is fully faded out under it.
const SCROLL_BUTTON_ZONE = '2rem';
const FADE_TRANSITION = '150ms ease-out';

// Registered as lengths so the fade width animates instead of jumping.
const fadePropertyDeclarations = `
  @property --scroll-fade-start {
    syntax: '<length>';
    initial-value: 0px;
    inherits: false;
  }

  @property --scroll-fade-end {
    syntax: '<length>';
    initial-value: 0px;
    inherits: false;
  }
`;

// Each variable is an edge's whole faded zone: fully transparent up to the
// last `FADE_SIZE` of it, which ramps up to opaque. At 0 the ramp sits outside
// the box, so animating the variable slides the fade in from the edge.
const fadeMask = (direction: 'right' | 'bottom') =>
  `linear-gradient(to ${direction}, transparent calc(var(--scroll-fade-start) - ${FADE_SIZE}), black var(--scroll-fade-start), black calc(100% - var(--scroll-fade-end)), transparent calc(100% - var(--scroll-fade-end) + ${FADE_SIZE}))`;

interface ScrollButtonProps {
  side: 'left' | 'right';
  visible: boolean;
  onClick(): void;
}

function ScrollButton({ side, visible, onClick }: ScrollButtonProps) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      // Out of the tab order and the accessibility tree: focus moving through
      // the content scrolls it already.
      tabIndex={-1}
      aria-hidden
      // Leaves focus where it is (e.g. on a tab).
      onMouseDown={event => event.preventDefault()}
      onClick={onClick}
      className={cn(
        'absolute top-1/2 flex size-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded',
        'text-fg-muted transition-[opacity,visibility,color,background-color] duration-150 hover:bg-bg-inset/50 hover:text-fg-default',
        side === 'left' ? 'left-0.5' : 'right-0.5',
        visible ? 'visible opacity-100' : 'invisible opacity-0'
      )}
    >
      <Icon className="size-4" />
    </button>
  );
}

interface ScrollMetrics {
  offset: number;
  size: number;
  viewport: number;
}

const readMetrics = (el: HTMLElement, horizontal: boolean): ScrollMetrics =>
  horizontal
    ? { offset: el.scrollLeft, size: el.scrollWidth, viewport: el.clientWidth }
    : {
        offset: el.scrollTop,
        size: el.scrollHeight,
        viewport: el.clientHeight
      };

export const ScrollShadowWrapper = forwardRef<
  HTMLDivElement,
  ScrollShadowWrapperProps
>(function ScrollShadowWrapper(props, forwardedRef) {
  const {
    children,
    className = '',
    style = {},
    orientation = 'vertical',
    edge = 'shadow',
    scrollerClassName,
    scrollButtons = false
  } = props;
  const horizontal = orientation === 'horizontal';

  const wrapperRef = useRef<HTMLDivElement>(null);
  // Only which edges are shadowed, never the metrics behind them: content that animates its size
  // moves `scrollHeight` every frame, and re-rendering the scroller for each of those frames is
  // what watching it for resizes would otherwise cost.
  // `bar`: the scroll bar's thickness, which the fade mask leaves whole.
  const [shadows, setShadows] = useState({ start: false, end: false, bar: 0 });

  const measure = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const { offset, size, viewport } = readMetrics(el, horizontal);
    const start = offset > 1; // tolerance
    const end = offset + viewport < size - 1; // tolerance
    // 0 for overlay scroll bars.
    const bar = horizontal
      ? el.offsetHeight - el.clientHeight
      : el.offsetWidth - el.clientWidth;
    setShadows(current =>
      current.start === start && current.end === end && current.bar === bar
        ? current // same object: React drops the re-render
        : { start, end, bar }
    );
  }, [horizontal]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    // One measure per frame at most: changes that land together read the same
    // layout, and a ResizeObserver that measures synchronously trips its own
    // loop guard.
    let scheduled = 0;
    const schedule = () => {
      if (scheduled) return;
      scheduled = requestAnimationFrame(() => {
        scheduled = 0;
        measure();
      });
    };

    // Only real size changes count: the scroller's own box, and its direct
    // children (content growing or shrinking inside it). Scrolling is handled by
    // `onScroll`.
    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(el);
    for (const child of el.children) resizeObserver.observe(child);
    // Rows added or removed: watch the new ones, drop the old ones.
    const childObserver = new MutationObserver(records => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node instanceof Element) resizeObserver.observe(node);
        }
        for (const node of record.removedNodes) {
          if (node instanceof Element) resizeObserver.unobserve(node);
        }
      }
      schedule();
    });
    childObserver.observe(el, { childList: true });

    measure();
    return () => {
      if (scheduled) cancelAnimationFrame(scheduled);
      resizeObserver.disconnect();
      childObserver.disconnect();
    };
  }, [measure]);

  const hasStartShadow = shadows.start;
  const hasEndShadow = shadows.end;
  const renderStart =
    (horizontal ? props.renderLeftShadow : props.renderTopShadow) !== false;
  const renderEnd =
    (horizontal ? props.renderRightShadow : props.renderBottomShadow) !== false;

  const soft = edge === 'soft';
  const fade = edge === 'fade' || soft;
  const withButtons = fade && horizontal && scrollButtons;
  const zone = withButtons
    ? `calc(${SCROLL_BUTTON_ZONE} + ${FADE_SIZE})`
    : FADE_SIZE;
  const startZone = renderStart ? zone : '0px';
  const endZone = renderEnd ? zone : '0px';
  // The mask is relative to the scroller's box, so it stays at the edges while
  // the content scrolls under it.
  const mask = fadeMask(horizontal ? 'right' : 'bottom');
  // `mask-image` covers the scroll bar too: a second, solid layer over its
  // strip keeps it whole.
  const bar = `${shadows.bar}px`;
  const maskLayers = {
    image: `${mask}, linear-gradient(black, black)`,
    size: horizontal
      ? `100% calc(100% - ${bar}), 100% ${bar}`
      : `calc(100% - ${bar}) 100%, ${bar} 100%`,
    position: horizontal ? 'left top, left bottom' : 'left top, right top'
  };
  const fadeStyle = fade
    ? ({
        '--scroll-fade-start': hasStartShadow ? startZone : '0px',
        '--scroll-fade-end': hasEndShadow ? endZone : '0px',
        scrollPadding: horizontal
          ? `0 ${endZone} 0 ${startZone}`
          : `${startZone} 0 ${endZone}`,
        maskImage: maskLayers.image,
        WebkitMaskImage: maskLayers.image,
        maskSize: maskLayers.size,
        WebkitMaskSize: maskLayers.size,
        maskPosition: maskLayers.position,
        WebkitMaskPosition: maskLayers.position,
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        transition: `--scroll-fade-start ${FADE_TRANSITION}, --scroll-fade-end ${FADE_TRANSITION}`
      } as CSSProperties)
    : undefined;
  const fadeProperties = fade && <style>{fadePropertyDeclarations}</style>;

  // A page less both faded edges: what sat under one edge lands clear of the
  // other.
  const scrollPage = (direction: -1 | 1) => {
    const el = wrapperRef.current;
    if (!el) return;
    const { scrollPaddingLeft, scrollPaddingRight } = getComputedStyle(el);
    const covered =
      (parseFloat(scrollPaddingLeft) || 0) +
      (parseFloat(scrollPaddingRight) || 0);
    el.scrollBy({
      left: direction * Math.max(el.clientWidth - covered, el.clientWidth / 2),
      behavior: 'smooth'
    });
  };

  if (soft) {
    // The shadows sit beside the scroller, not in it: the mask would fade them
    // out together with the content. Not over the scroll bar.
    const shadowClass = (visible: boolean) =>
      cn(
        'pointer-events-none absolute transition-opacity duration-150',
        visible ? 'opacity-100' : 'opacity-0'
      );
    return (
      <div className={cn('relative flex min-h-0 min-w-0 flex-col', className)}>
        <div
          ref={mergeRefs(wrapperRef, forwardedRef)}
          style={{ ...fadeStyle, ...style }}
          className={cn(
            'min-h-0 min-w-0',
            horizontal ? 'overflow-x-auto' : 'flex-1 overflow-y-auto',
            scrollerClassName
          )}
          onScroll={measure}
          data-has-top-shadow={!horizontal && hasStartShadow ? '' : undefined}
          data-has-bottom-shadow={!horizontal && hasEndShadow ? '' : undefined}
          data-has-left-shadow={horizontal && hasStartShadow ? '' : undefined}
          data-has-right-shadow={horizontal && hasEndShadow ? '' : undefined}
        >
          {fadeProperties}
          {children}
        </div>

        {withButtons ? (
          <>
            {renderStart && (
              <ScrollButton
                side="left"
                visible={hasStartShadow}
                onClick={() => scrollPage(-1)}
              />
            )}
            {renderEnd && (
              <ScrollButton
                side="right"
                visible={hasEndShadow}
                onClick={() => scrollPage(1)}
              />
            )}
          </>
        ) : (
          <>
            {renderStart && (
              <div
                aria-hidden
                data-edge-shadow={hasStartShadow ? 'visible' : 'hidden'}
                style={
                  horizontal ? { bottom: shadows.bar } : { right: shadows.bar }
                }
                className={cn(
                  shadowClass(hasStartShadow),
                  horizontal
                    ? 'inset-y-0 left-0 w-2 bg-gradient-to-r'
                    : 'left-0 top-0 h-2 bg-gradient-to-b',
                  'from-fg-default/5 to-transparent'
                )}
              />
            )}
            {renderEnd && (
              <div
                aria-hidden
                data-edge-shadow={hasEndShadow ? 'visible' : 'hidden'}
                style={
                  horizontal ? { bottom: shadows.bar } : { right: shadows.bar }
                }
                className={cn(
                  shadowClass(hasEndShadow),
                  horizontal
                    ? 'inset-y-0 right-0 w-2 bg-gradient-to-l'
                    : 'bottom-0 left-0 h-2 bg-gradient-to-t',
                  'from-fg-default/5 to-transparent'
                )}
              />
            )}
          </>
        )}
      </div>
    );
  }

  if (horizontal) {
    const scroller = (
      <div
        ref={mergeRefs(wrapperRef, forwardedRef)}
        style={{ ...fadeStyle, ...style }}
        className={cn('relative overflow-x-auto', className)}
        onScroll={measure}
        data-has-left-shadow={hasStartShadow ? '' : undefined}
        data-has-right-shadow={hasEndShadow ? '' : undefined}
      >
        {fadeProperties}
        {children}
      </div>
    );
    if (fade && !withButtons) return scroller;

    // The edge overlays sit on a wrapper: inside the scroller, they would
    // scroll away with the content.
    return (
      <div className="relative min-w-0">
        {scroller}

        {withButtons ? (
          <>
            {renderStart && (
              <ScrollButton
                side="left"
                visible={hasStartShadow}
                onClick={() => scrollPage(-1)}
              />
            )}
            {renderEnd && (
              <ScrollButton
                side="right"
                visible={hasEndShadow}
                onClick={() => scrollPage(1)}
              />
            )}
          </>
        ) : (
          <>
            {renderStart && (
              <div
                className={cn(
                  'z-1000 pointer-events-none absolute inset-y-0 left-0 w-1.5 transition-opacity duration-100',
                  'border-l border-border-default',
                  'bg-gradient-to-r from-black/5 to-transparent',
                  hasStartShadow ? 'opacity-100' : 'opacity-0'
                )}
              />
            )}
            {renderEnd && (
              <div
                className={cn(
                  'z-1000 pointer-events-none absolute inset-y-0 right-0 w-1.5 transition-opacity duration-100',
                  'border-r border-border-default',
                  'bg-gradient-to-l from-black/5 to-transparent',
                  hasEndShadow ? 'opacity-100' : 'opacity-0'
                )}
              />
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div
      ref={mergeRefs(wrapperRef, forwardedRef)}
      style={{ ...fadeStyle, ...style }}
      className={cn('relative overflow-y-auto', className)}
      onScroll={measure}
      data-has-top-shadow={hasStartShadow ? '' : undefined}
      data-has-bottom-shadow={hasEndShadow ? '' : undefined}
    >
      {fadeProperties}

      {!fade && renderStart && (
        <div
          className={cn(
            // `shrink-0`: in a flex-column scroller the empty bar would shrink to its border.
            'z-1000 pointer-events-none sticky top-0 -mb-1.5 h-1.5 w-full shrink-0 transition-opacity duration-100 ',
            'border-t border-border-default',
            'bg-gradient-to-b from-black/5 to-transparent',
            hasStartShadow ? 'opacity-100' : 'opacity-0'
          )}
        />
      )}

      {children}

      {!fade && renderEnd && (
        <div
          className={cn(
            'z-1000 pointer-events-none sticky bottom-0 -mt-1.5 h-1.5 w-full shrink-0 transition-opacity duration-100',
            'border-b border-border-default',
            'bg-gradient-to-t from-black/5 to-transparent',
            hasEndShadow ? 'opacity-100' : 'opacity-0'
          )}
        />
      )}
    </div>
  );
});
ScrollShadowWrapper.displayName = 'ScrollShadowWrapper';
